import React, { useEffect, useRef, useState } from "react";
import { Pagination, Select, Skeleton, Modal } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import TemplateRow from "./TemplateRow";
import {
  formatDate,
  formatTime,
  getValueForInput,
  loadingStates,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import Editor from "../Editor";
import {
  templatesDetailsApi,
  templatesListApi,
  deleteTemplate,
} from "../../utilities/apis/templates";
import { selectUserData } from "../../redux/selectors";
import { useSelector } from "react-redux";
import { getDocsToken } from "../../utilities/apis/agreements";
import constants from "../../utilities/constants";

const toPlaceholderList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    try {
      return toPlaceholderList(JSON.parse(value));
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => value[key])
      .filter(Boolean)
      .map(String);
  }
  return [];
};

const TemplateRowSkeleton = ({ compact = false, showActions = true }) => (
  <div
    className={`flex items-center justify-between gap-4 border border-slate-200 bg-white ${
      compact ? "mb-2 rounded-lg px-3.5 py-3" : "mb-3 rounded-xl px-4 py-4"
    }`}
  >
    <div className="flex min-w-0 flex-1 items-start gap-3">
      <Skeleton
        height={compact ? 36 : 44}
        width={compact ? 36 : 44}
        radius="md"
        className="shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton height={16} className="w-2/5 max-w-xs" radius="sm" />
        <Skeleton height={12} width={180} radius="sm" />
      </div>
    </div>
    {showActions && (
      <div className="flex shrink-0 gap-2">
        <Skeleton height={36} width={88} radius="md" />
        <Skeleton height={36} width={72} radius="md" />
      </div>
    )}
  </div>
);

const TemplateModalSkeleton = () => (
  <div className="flex flex-col gap-5">
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Skeleton height={44} width={44} radius="md" className="shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton height={20} className="w-2/5 max-w-sm" radius="sm" />
          <Skeleton height={12} width={220} radius="sm" />
        </div>
      </div>
      <Skeleton height={24} width={80} radius="xl" />
    </div>
    <div className="min-h-[360px] rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mx-auto flex max-w-2xl flex-col gap-2.5">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            height={12}
            radius="sm"
            className={
              index % 3 === 0
                ? "w-full"
                : index % 3 === 1
                  ? "w-11/12"
                  : "w-10/12"
            }
          />
        ))}
      </div>
    </div>
  </div>
);

function TemplatesList({
  useTemplateFunc,
  templateType = "Notices",
  showNewTemplateBtn = true,
  showEditButton = false,
  showViewButton = true,
  showUseButton,
  pickerMode = false,
  placeholders,
  setPlaceholders,
}) {
  const currentUser = useSelector(selectUserData) || {};

  const [templatesConfig, setTemplatesConfig] = useState({
    loading: loadingStates.LOADING,
    templates: null,
    page: 1,
    templatesCount: null,
    templateType,
    templateCategory: "All",
    templateCategoriesList: ["All"],
    templateToView: null,
    templateToViewData: null,
    templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
  });
  const [sfdtContent, setSfdtContent] = useState(null);
  const [previewPlaceholders, setPreviewPlaceholders] = useState([]);
  const [usingTemplateId, setUsingTemplateId] = useState(null);
  const [viewingTemplateId, setViewingTemplateId] = useState(null);
  const editorRef = useRef();
  const canUseTemplate = !!useTemplateFunc;
  const showUseButtonOnRows = showUseButton ?? canUseTemplate;
  const isListLoading = templatesConfig.loading === loadingStates.LOADING;
  const isModalLoading =
    templatesConfig.templateToViewLoading === loadingStates.LOADING;
  const skeletonCount = pickerMode ? 6 : 8;

  const getTemplateDetails = async (templateId) => {
    setTemplatesConfig((prevState) => ({
      ...prevState,
      templateToViewLoading: loadingStates.LOADING,
      templateToViewData: null,
    }));
    setSfdtContent(null);
    setPreviewPlaceholders([]);

    try {
      const response = await apiWrapWithErrorWithData(
        templatesDetailsApi({ templateId }),
      );

      if (response?.success && response?.template) {
        setTemplatesConfig((prevState) => ({
          ...prevState,
          templateToViewData: response.template,
        }));
        const fieldNames = toPlaceholderList(response.template.fieldName);
        setPreviewPlaceholders(fieldNames);
        setPlaceholders?.(fieldNames);

        const accessToken = await apiWrapWithErrorWithData(getDocsToken());
        const response2 = await fetch(
          `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/items/${response.template.quillDelta}/content`,
          {
            headers: {
              Authorization: `Bearer ${accessToken.data}`,
            },
          },
        );

        if (response2.headers.get("Content-Length")) {
          const blob = await response2.blob();
          const file = new File([blob], "doc.docx", {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          const formData = new FormData();
          formData.append("files", file);

          const sfdtResponse = await fetch(
            `${constants.wordeditorApi}/api/documenteditor/Import`,
            { method: "POST", body: formData },
          );

          const sfdt = await sfdtResponse.json();
          setSfdtContent(sfdt);
        }
      } else {
        showNotification({
          color: "red",
          title: "Template",
          message: "Failed to load template details.",
        });
        setTemplatesConfig((prevState) => ({
          ...prevState,
          templateToView: null,
        }));
      }
    } catch {
      showNotification({
        color: "red",
        title: "Template",
        message: "Failed to load template preview.",
      });
      setTemplatesConfig((prevState) => ({
        ...prevState,
        templateToView: null,
      }));
    } finally {
      setTemplatesConfig((prevState) => ({
        ...prevState,
        templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
      }));
      setViewingTemplateId(null);
    }
  };

  const getTemplatesList = async () => {
    setTemplatesConfig((prevState) => ({
      ...prevState,
      loading: loadingStates.LOADING,
      templatesCount: null,
    }));

    const response = await apiWrapWithErrorWithData(
      templatesListApi({
        page: templatesConfig.page,
        type: templatesConfig.templateType,
        category:
          templatesConfig.templateCategory.toLowerCase() === "all"
            ? null
            : templatesConfig.templateCategory,
      }),
    );

    if (response?.success && response?.templates) {
      setTemplatesConfig((prevState) => ({
        ...prevState,
        templates: response.templates,
        loading: loadingStates.NO_ACTIVE_REQUEST,
        templatesCount: response.templatesCount || null,
        templateCategoriesList: [
          "All",
          ...response.templateCategories.map((template) => template.category),
        ],
      }));
    } else {
      setTemplatesConfig((prevState) => ({
        ...prevState,
        loading: loadingStates.NO_ACTIVE_REQUEST,
      }));
    }
  };

  useEffect(() => {
    getTemplatesList();
  }, [
    templatesConfig.page,
    templatesConfig.templateCategory,
    templatesConfig.templateType,
  ]);

  useEffect(() => {
    setTemplatesConfig((preState) => ({
      ...preState,
      templateType,
    }));
  }, [templateType]);

  useEffect(() => {
    if (templatesConfig.templateToView?.id) {
      getTemplateDetails(templatesConfig.templateToView.id);
    }
  }, [templatesConfig.templateToView]);

  const viewHandler = (template) => {
    setViewingTemplateId(template.id);
    setSfdtContent(null);
    setPreviewPlaceholders([]);
    setTemplatesConfig((prevState) => ({
      ...prevState,
      templateToView: template,
      templateToViewData: null,
      templateToViewLoading: loadingStates.LOADING,
    }));
  };

  const templateDetails = templatesConfig.templateToViewData;
  const navigate = useNavigate();

  const useTemplateHandler = useTemplateFunc
    ? () => {
        setTemplatesConfig((prevState) => ({
          ...prevState,
          templateToView: null,
          templateToViewData: null,
        }));
        useTemplateFunc(templatesConfig.templateToViewData);
      }
    : null;

  const useTemplateDirectHandler = async (template) => {
    if (!useTemplateFunc) return;

    setUsingTemplateId(template.id);
    try {
      const response = await apiWrapWithErrorWithData(
        templatesDetailsApi({ templateId: template.id }),
      );

      if (response?.success && response?.template) {
        setPlaceholders?.(response.template.fieldName || []);
        useTemplateFunc(response.template);
        return;
      }

      showNotification({
        color: "red",
        title: "Template",
        message: "Could not load the selected template",
      });
    } catch {
      showNotification({
        color: "red",
        title: "Template",
        message: "Could not load the selected template",
      });
    } finally {
      setUsingTemplateId(null);
    }
  };

  const deleteHandler = async (id) => {
    try {
      const response = await apiWrapWithErrorWithData(
        deleteTemplate({ templateId: id }),
      );

      if (response.success) {
        showNotification({
          color: "green",
          title: "Templates",
          message: "Template is deleted",
        });
      } else {
        showNotification({
          color: "red",
          title: "Templates",
          message: "Unable to delete the template",
        });
      }
      getTemplatesList();
    } catch {
      showNotification({
        color: "red",
        title: "Templates",
        message: "Unable to delete the template",
      });
      getTemplatesList();
    }
  };

  const canCreateTemplate =
    showNewTemplateBtn && [1, 2, 4].includes(currentUser.loginAs?.id);

  const pageTitle = `${templateType.charAt(0).toUpperCase() + templateType.slice(1)} templates`;

  const closePreview = () => {
    setViewingTemplateId(null);
    setTemplatesConfig((prevState) => ({
      ...prevState,
      templateToView: null,
      templateToViewData: null,
      templateToViewLoading: loadingStates.NO_ACTIVE_REQUEST,
    }));
    setSfdtContent(null);
    setPreviewPlaceholders([]);
  };

  return (
    <>
      {templatesConfig.templateToView && (
        <Modal
          overflow="inside"
          opened
          onClose={closePreview}
          padding={0}
          withCloseButton={false}
          classNames={{
            modal: "rounded-2xl overflow-hidden flex flex-col",
            body: "p-0 flex-1 flex-col overflow-hidden",
          }}
          styles={{
            modal: {
              height: "90vh",
              maxHeight: "90vh",
              width: "90vw",
              maxWidth: "90vw",
            },
          }}
        >
          <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Template preview
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Review the document before applying it
              </p>
            </div>
            <button
              type="button"
              onClick={closePreview}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              aria-label="Close preview"
            >
              <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-3">
            {isModalLoading && (
              <div className="flex flex-col gap-3">
                <p className="text-center text-sm text-slate-500">
                  Loading template preview...
                </p>
                <TemplateModalSkeleton />
              </div>
            )}

            {!isModalLoading && !!templateDetails && !!sfdtContent && (
              <div className="flex flex-1 flex-col gap-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#46BDE1]/10">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        className="h-5 w-5 text-[#46BDE1]"
                      >
                        <path
                          d="M7 3.75h6.75L18.25 8.25V20.25H7V3.75Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.75 3.75V8.25H18.25"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3
                        className="truncate text-base font-semibold text-slate-900"
                        title={templateDetails.name}
                      >
                        {templateDetails.name}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                        {templateDetails.createdBy?.name && (
                          <span>
                            Created by {templateDetails.createdBy.name}
                          </span>
                        )}
                        <span>
                          {formatDate(templateDetails.createdAt)},{" "}
                          {formatTime(templateDetails.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {templateDetails.category && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-600">
                        {templateDetails.category}
                      </span>
                    )}
                    {!!useTemplateHandler && (
                      <button
                        type="button"
                        onClick={useTemplateHandler}
                        className="inline-flex h-9 items-center rounded-lg bg-[#46BDE1] px-4 text-sm font-medium text-white hover:bg-[#3aa8ca]"
                      >
                        Use this template
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white p-2">
                  <Editor
                    placeholders={previewPlaceholders}
                    setPlaceholders={setPreviewPlaceholders}
                    disabled
                    preview
                    ref={editorRef}
                    initialContent={sfdtContent}
                    editNotAllowed
                  />
                </div>
              </div>
            )}

            {!isModalLoading && !!templateDetails && !sfdtContent && (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-8 py-10 text-center">
                <p className="text-sm font-medium text-slate-700">
                  Preview unavailable
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Unable to load the document preview for this template.
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}

      <div className={`flex w-full flex-col ${pickerMode ? "gap-3" : "gap-5"}`}>
        {!pickerMode ? (
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                {pageTitle}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Browse, preview, and manage your {templateType.toLowerCase()}{" "}
                templates
                {templatesConfig.templatesCount
                  ? ` · ${templatesConfig.templatesCount} total`
                  : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select
                disabled={isListLoading}
                className="w-52"
                placeholder="All categories"
                data={templatesConfig.templateCategoriesList}
                value={templatesConfig.templateCategory || "All"}
                onChange={(input) => {
                  const val = getValueForInput(input);
                  setTemplatesConfig((prevState) => ({
                    ...prevState,
                    templateCategory: val,
                    page: 1,
                  }));
                }}
              />
              {canCreateTemplate && (
                <button
                  type="button"
                  onClick={() =>
                    navigate("/app/templates/agreements_manager/agreements/new")
                  }
                  className="inline-flex h-10 items-center rounded-lg bg-[#46BDE1] px-4 text-sm font-medium text-white hover:bg-[#3aa8ca]"
                >
                  New template
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm font-medium text-slate-700">
              Filter templates
            </p>
            <Select
              disabled={isListLoading}
              className="w-full max-w-xs"
              placeholder="All categories"
              data={templatesConfig.templateCategoriesList}
              value={templatesConfig.templateCategory || "All"}
              onChange={(input) => {
                const val = getValueForInput(input);
                setTemplatesConfig((prevState) => ({
                  ...prevState,
                  templateCategory: val,
                  page: 1,
                }));
              }}
            />
          </div>
        )}

        <div
          className={
            pickerMode ? "max-h-[min(52vh,520px)] overflow-y-auto pr-1" : ""
          }
        >
          {isListLoading &&
            Array.from({ length: skeletonCount }).map((_, index) => (
              <TemplateRowSkeleton
                key={index}
                compact={pickerMode}
                showActions={
                  showViewButton || showUseButtonOnRows || showEditButton
                }
              />
            ))}

          {!isListLoading &&
            templatesConfig.templates?.map((template) => (
              <TemplateRow
                key={template.id}
                {...template}
                editButton={showEditButton}
                viewButton={showViewButton}
                useButton={showUseButtonOnRows}
                useLoading={usingTemplateId === template.id}
                viewLoading={viewingTemplateId === template.id}
                compact={pickerMode}
                onDelete={deleteHandler}
                onViewClick={() => viewHandler(template)}
                onUseClick={() => useTemplateDirectHandler(template)}
              />
            ))}

          {!isListLoading && templatesConfig.templates?.length === 0 && (
            <div
              className={`flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-center ${
                pickerMode ? "min-h-[180px] px-6" : "min-h-[240px] px-8"
              }`}
            >
              <p className="text-sm font-medium text-slate-700">
                No templates found
              </p>
              <p className="mt-1 max-w-sm text-sm text-slate-500">
                {canCreateTemplate
                  ? "Create a new template to start using it in agreements."
                  : "Try a different category or check back later."}
              </p>
              {canCreateTemplate && (
                <button
                  type="button"
                  onClick={() =>
                    navigate("/app/templates/agreements_manager/agreements/new")
                  }
                  className="mt-4 inline-flex h-9 items-center rounded-lg bg-[#46BDE1] px-4 text-sm font-medium text-white hover:bg-[#3aa8ca]"
                >
                  New template
                </button>
              )}
            </div>
          )}
        </div>

        {templatesConfig.templatesCount > 0 && (
          <div className="flex justify-center pt-2">
            <Pagination
              size="sm"
              onChange={(page) => {
                setTemplatesConfig((prevState) => ({
                  ...prevState,
                  page,
                }));
              }}
              total={Math.ceil(templatesConfig.templatesCount / 10)}
              page={templatesConfig.page}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default TemplatesList;
